import { useCallback, useRef, useState } from 'react';
import { ImageIcon, Loader2, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { api, unwrap } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { MediaPicker } from './MediaPicker';
import { cn } from '@/lib/cn';

interface MediaAsset {
  id: string;
  url: string;
  original_name: string;
  mime_type: string;
}

interface ImageDropzoneProps {
  value: string | null;
  onChange: (url: string | null) => void;
  /** Optional alt that is shown on the preview <img>. */
  alt?: string;
  /** Aspect ratio for the empty state, e.g. 'aspect-video' (default) or 'aspect-square'. */
  aspectClass?: string;
  /** Max upload size in MB. Defaults to 20. */
  maxMb?: number;
  /** Show the "Browse library" secondary action. Defaults true. */
  showLibrary?: boolean;
  /** Label for the empty state. */
  label?: string;
}

const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'image/avif', 'image/gif', 'image/svg+xml'];

/**
 * Image picker with three input methods:
 *   1) Click → opens the MediaPicker library.
 *   2) Drag-and-drop a file from the desktop onto the dropzone.
 *   3) "Subir" button → native file picker.
 *
 * Uploads go to POST /api/v1/media and the returned `url` is propagated via `onChange`.
 */
export function ImageDropzone({
  value,
  onChange,
  alt = '',
  aspectClass = 'aspect-video',
  maxMb = 20,
  showLibrary = true,
  label = 'Establecer imagen destacada',
}: ImageDropzoneProps) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const validate = useCallback((file: File): string | null => {
    if (!ACCEPTED.includes(file.type)) return 'Formato no soportado. Usa JPG, PNG, WebP, AVIF, GIF o SVG.';
    if (file.size > maxMb * 1024 * 1024) return `Archivo demasiado grande (máx ${maxMb} MB).`;
    return null;
  }, [maxMb]);

  const upload = useCallback(async (file: File) => {
    const error = validate(file);
    if (error) {
      toast.error(error);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const asset = await unwrap<MediaAsset>(
        api.post('/media', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
      );
      onChange(asset.url);
      toast.success('Imagen subida');
    } catch {
      toast.error('No se pudo subir la imagen');
    } finally {
      setUploading(false);
    }
  }, [onChange, validate]);

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    const file = Array.from(e.dataTransfer.files ?? []).find((f) => f.type.startsWith('image/'));
    if (file) void upload(file);
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (!dragging) setDragging(true);
  }

  function handleDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  }

  function handleBrowse() {
    fileInput.current?.click();
  }

  function handleFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) void upload(file);
    if (fileInput.current) fileInput.current.value = '';
  }

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragEnter={handleDragOver}
      onDragLeave={handleDragLeave}
      className="space-y-2"
    >
      {value ? (
        <div className={cn('relative overflow-hidden rounded-md border border-neutral-200 bg-neutral-50', aspectClass)}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={value} alt={alt} className="h-full w-full object-cover" />
          {dragging && (
            <div className="pointer-events-none absolute inset-0 grid place-items-center bg-neutral-900/70 text-white text-sm">
              Suelta para reemplazar
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={handleBrowse}
          disabled={uploading}
          className={cn(
            'flex w-full flex-col items-center justify-center gap-1.5 rounded-md',
            'border-2 border-dashed text-xs transition-colors',
            aspectClass,
            dragging
              ? 'border-neutral-900 bg-neutral-100 text-neutral-900'
              : 'border-neutral-300 bg-neutral-50 text-neutral-500 hover:border-neutral-400 hover:text-neutral-700',
            uploading && 'cursor-wait opacity-70',
          )}
        >
          {uploading ? (
            <>
              <Loader2 className="size-6 animate-spin" />
              Subiendo…
            </>
          ) : (
            <>
              <ImageIcon className="size-6" />
              <span className="text-center px-2">
                {dragging ? 'Suelta la imagen' : label}
              </span>
              <span className="text-[10px] text-neutral-400">
                Arrastra una imagen aquí o pulsa para elegirla
              </span>
            </>
          )}
        </button>
      )}

      <div className="flex gap-1.5">
        {value ? (
          <>
            <Button size="sm" variant="outline" className="flex-1" onClick={handleBrowse} disabled={uploading}>
              <Upload className="size-3.5" /> Cambiar
            </Button>
            {showLibrary && (
              <Button size="sm" variant="ghost" onClick={() => setLibraryOpen(true)} title="Elegir de la biblioteca">
                <ImageIcon className="size-3.5" />
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onChange(null)}
              className="text-red-600 hover:bg-red-50"
              title="Quitar imagen"
            >
              <Trash2 className="size-3.5" />
            </Button>
          </>
        ) : (
          <>
            <Button size="sm" variant="outline" className="flex-1" onClick={handleBrowse} disabled={uploading}>
              <Upload className="size-3.5" /> Subir desde el equipo
            </Button>
            {showLibrary && (
              <Button size="sm" variant="ghost" onClick={() => setLibraryOpen(true)}>
                Biblioteca
              </Button>
            )}
          </>
        )}
      </div>

      <input
        ref={fileInput}
        type="file"
        accept={ACCEPTED.join(',')}
        className="hidden"
        onChange={handleFileInput}
      />

      {libraryOpen && (
        <MediaPicker
          open={libraryOpen}
          onClose={() => setLibraryOpen(false)}
          onSelect={(url) => {
            onChange(url);
            setLibraryOpen(false);
          }}
        />
      )}
    </div>
  );
}
